import { z } from 'zod';

/**
 * Turning a device's management protocols on, from here.
 *
 * Monitoring a router needs SNMP reachable from this server. That is two
 * separate things on the device: the SNMP service enabled with a community we
 * know, and a firewall rule that lets our address reach UDP 161. Doing it by
 * hand across a fleet is the reason six MikroTiks sat at "SNMP: Request timed
 * out" — so this drives it over the SSH/telnet session the platform already
 * has.
 *
 * Every command this builds is *additive*. It never deletes a firewall rule
 * and edits only the ones carrying our own marker comment, and the rule it
 * adds is an `accept` scoped to one source address — an accept rule cannot
 * lock an operator out, which is the property that makes this safe to run
 * against a production edge router.
 */

export const PROVISION_PROTOCOLS = ['snmp', 'ssh', 'telnet'] as const;
export type ProvisionProtocol = (typeof PROVISION_PROTOCOLS)[number];

/** Marker on every object we create, so a re-run updates instead of stacking. */
export const PROVISION_MARKER = 'itr-managed';

export const provisionRequestSchema = z.object({
  protocol: z.enum(PROVISION_PROTOCOLS),
  /** Where to run the commands. Telnet is the fallback for gear with SSH off. */
  transport: z.enum(['ssh', 'telnet']).default('ssh'),
  username: z.string().min(1).optional(),
  password: z.string().optional(),
  /** Pull credentials from this vault entry instead of `username`/`password`. */
  vaultEntryId: z.string().optional(),
  /**
   * Source address allowed to reach the service, as the device sees us.
   *
   * Left blank, the server asks the device itself (`/user active print terse`)
   * which address our session arrived from. It deliberately does *not* use the
   * local end of its own socket: with Docker masquerading the container and a
   * VPN gateway NATing again beyond that, the socket's local address
   * (`172.17.0.2`) is one the device has never seen, and a rule scoped to it
   * matches nothing — the device stays silently unreachable on UDP 161.
   */
  sourceAddress: z.string().optional(),
  /** SNMP only. */
  community: z.string().optional(),
  snmpVersion: z.enum(['1', '2c', '3']).optional(),
  /** false = preview the commands without touching the device. */
  apply: z.boolean().default(false),
});
export type ProvisionRequest = z.infer<typeof provisionRequestSchema>;

export interface ProvisionStep {
  /** What this step is for, in an operator's words. */
  title: string;
  command: string;
  /** Reads state only — safe regardless of whether the operator applies. */
  readOnly: boolean;
  /**
   * Needs root on the far end. Only meaningful on a Linux host, where the
   * caller wraps the command in `sudo` when the SSH login is not root itself;
   * a RouterOS login either has the permission or does not.
   */
  root?: boolean;
}

export interface ProvisionResult {
  protocol: ProvisionProtocol;
  vendor: string;
  applied: boolean;
  sourceAddress: string | null;
  steps: Array<ProvisionStep & { output?: string; ok?: boolean; error?: string }>;
  /** Post-run check that the protocol actually answers now. */
  verified: boolean | null;
  verifyMessage: string | null;
}

/* ------------------------------------------------------------------ */
/* Input safety                                                        */
/* ------------------------------------------------------------------ */

/**
 * These values are interpolated into a command line executed on the device, so
 * they are validated as a whitelist rather than escaped. RouterOS quoting
 * rules are not the shell's, and "escape it correctly for two different
 * grammars" is a bet worth not taking.
 */
const COMMUNITY_RE = /^[A-Za-z0-9_-]{1,32}$/;
const SRC_ADDR_RE = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}(\/([0-9]|[12]\d|3[0-2]))?$/;

export function isValidCommunity(value: string): boolean {
  return COMMUNITY_RE.test(value);
}

export function isValidSourceAddress(value: string): boolean {
  return SRC_ADDR_RE.test(value);
}

/* ------------------------------------------------------------------ */
/* MikroTik / RouterOS                                                 */
/* ------------------------------------------------------------------ */

/**
 * Our own accept rule for one service, found by its marker comment. When it
 * already exists it is re-pointed at `cidr` rather than left alone: a rule
 * scoped to a stale address (an older run, or a source the device never sees
 * because the tunnel gateway NATs us) matches nothing, and "already present"
 * would otherwise keep a device silently unmonitored across every re-run.
 * Only the rule carrying our marker is touched.
 */
function mikrotikAcceptRule(name: string, protocol: 'udp' | 'tcp', port: number, cidr: string, place: string): string {
  const find = `[/ip firewall filter find comment="${PROVISION_MARKER}-${name}"]`;
  return (
    `:if ([:len ${find}] = 0) ` +
    `do={/ip firewall filter add chain=input protocol=${protocol} dst-port=${port} ` +
    `src-address=${cidr} action=accept comment="${PROVISION_MARKER}-${name}"${place}} ` +
    `else={/ip firewall filter set ${find} src-address=${cidr}}`
  );
}

/**
 * RouterOS command set to make SNMP reachable from `sourceAddress`.
 *
 * `dropRuleId` comes from a prior read of the input chain. A firewall's input
 * chain is evaluated in order, so an accept appended after a catch-all drop
 * would never be reached — when a drop exists we place the new rule before it,
 * and when it does not, a plain append is already in the right place.
 */
export function mikrotikSnmpSteps(opts: {
  community: string;
  sourceAddress: string;
  dropRuleId?: string | null;
}): ProvisionStep[] {
  const { community, sourceAddress } = opts;
  const cidr = sourceAddress.includes('/') ? sourceAddress : `${sourceAddress}/32`;
  const place = opts.dropRuleId ? ` place-before=${opts.dropRuleId}` : '';

  return [
    {
      title: 'Create or update the SNMP community, restricted to this server',
      command:
        `:if ([:len [/snmp community find name="${community}"]] = 0) ` +
        `do={/snmp community add name="${community}" addresses=${cidr}} ` +
        `else={/snmp community set [find name="${community}"] addresses=${cidr}}`,
      readOnly: false,
    },
    {
      title: 'Enable the SNMP service',
      command: `/snmp set enabled=yes`,
      readOnly: false,
    },
    {
      title: 'Allow UDP 161 from this server (re-pointed at this address if already present)',
      command: mikrotikAcceptRule('snmp', 'udp', 161, cidr, place),
      readOnly: false,
    },
  ];
}

/** Enable the SSH service and let this server reach it. */
export function mikrotikSshSteps(opts: { sourceAddress: string; dropRuleId?: string | null }): ProvisionStep[] {
  const cidr = opts.sourceAddress.includes('/') ? opts.sourceAddress : `${opts.sourceAddress}/32`;
  const place = opts.dropRuleId ? ` place-before=${opts.dropRuleId}` : '';
  return [
    { title: 'Enable the SSH service', command: '/ip service enable ssh', readOnly: false },
    {
      title: 'Allow TCP 22 from this server (re-pointed at this address if already present)',
      command: mikrotikAcceptRule('ssh', 'tcp', 22, cidr, place),
      readOnly: false,
    },
  ];
}

/**
 * Enable telnet. Offered because plenty of older gear is only reachable that
 * way, but it is a plaintext protocol — the UI says so before this runs.
 */
export function mikrotikTelnetSteps(opts: { sourceAddress: string; dropRuleId?: string | null }): ProvisionStep[] {
  const cidr = opts.sourceAddress.includes('/') ? opts.sourceAddress : `${opts.sourceAddress}/32`;
  const place = opts.dropRuleId ? ` place-before=${opts.dropRuleId}` : '';
  return [
    { title: 'Enable the telnet service', command: '/ip service enable telnet', readOnly: false },
    {
      title: 'Allow TCP 23 from this server (re-pointed at this address if already present)',
      command: mikrotikAcceptRule('telnet', 'tcp', 23, cidr, place),
      readOnly: false,
    },
  ];
}

/** Read-only probe run before anything else, to confirm we are on RouterOS. */
export const MIKROTIK_IDENTIFY_COMMAND = '/system resource print';

/** Read-only: the first catch-all drop in the input chain, if any. */
export const MIKROTIK_FIND_DROP_COMMAND =
  '/ip firewall filter print terse where chain=input and (action=drop or action=reject)';

/**
 * Read-only: who is logged in and from where.
 *
 * This is how we learn the address the device actually sees us on. The socket's
 * own local address is not that address the moment anything NATs in between —
 * see `parseActiveUserAddress`.
 */
export const MIKROTIK_ACTIVE_USERS_COMMAND = '/user active print terse';

/**
 * The address the device sees our session coming from.
 *
 * `/user active print terse` prints one row per session, e.g.
 * `0 when=2026-09-06 01:40:47 name=admin address=192.168.50.1 via=ssh group=full`.
 * Rows are listed oldest-first, and ours is the one just created by logging in,
 * so the *last* row matching our username and transport is ours. Anything
 * else — an operator with a second session open from a different address — is
 * ignored rather than guessed at.
 */
export function parseActiveUserAddress(terseOutput: string, username: string, via: 'ssh' | 'telnet'): string | null {
  let found: string | null = null;
  for (const line of terseOutput.split(/\r?\n/)) {
    const name = /\bname=("[^"]*"|\S+)/.exec(line)?.[1]?.replace(/"/g, '');
    const address = /\baddress=(\S+)/.exec(line)?.[1];
    const transport = /\bvia=(\S+)/.exec(line)?.[1];
    if (name !== username || transport !== via || !address) continue;
    // RouterOS reports a bare IP here, but strip a port if a future version
    // ever appends one rather than feeding "1.2.3.4:52022" into a firewall rule.
    const bare = address.split(':')[0]!;
    if (isValidSourceAddress(bare)) found = bare;
  }
  return found;
}

/**
 * Pull the first rule id out of `print terse` output.
 *
 * Terse lines start with the ordinal then flags, e.g. `12 X ;;; comment` — the
 * id RouterOS accepts for `place-before` is that leading number.
 */
export function parseFirstRuleId(terseOutput: string): string | null {
  for (const line of terseOutput.split(/\r?\n/)) {
    const match = /^\s*(\d+)\s/.exec(line);
    if (match) return match[1]!;
  }
  return null;
}
